import { Align, ITableColumn, Justify, Label, Row, Table, TextAlign } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import BasicPicker from 'components/foundation/pickers/basic-picker/BasicPicker/BasicPicker';
import TimeAndSuperscript from 'components/foundation/text/TimeAndSuperscript/TimeAndSuperscript';
import { DateTime } from 'luxon';
import { ReactNode, useState } from 'react';
import MemberLocationType from 'types/enum/MemberLocationType';
import ILocationHistory from 'types/LocationHistory';
import { memberLocationsUtils } from 'utils';
import { dateTimeFromISOKeepZone, getDateString, isSameDay } from 'utils/dateUtils';
import { useFeatureFlags } from 'utils/features';
import { t } from 'utils/localize';
import './LocationHistoryTable.scss';

export interface ILocationHistoryTableProps {
  className?: string | object;
  data: ILocationHistory[];
  selectedId?: string;
  onRowClick?: (selectedId?: string) => void;
  onFilterChange?: (filter: string) => void;
}

const LocationHistoryTable = (props: ILocationHistoryTableProps) => {
  const { className, data, selectedId, onRowClick, onFilterChange } = props;

  const classes = classNames('location-history-table', className);

  const today = DateTime.local().startOf('day');
  const isPro = useFeatureFlags('PRO');
  const [locationFilter, setLocationFilter] = useState<string>(MemberLocationType.ALL);

  function renderColumns(): Array<ITableColumn<ILocationHistory>> {
    const columns = Array<ITableColumn<ILocationHistory>>();
    columns.push({
      cell: renderLocationColumn,
      key: 'location',
      align: TextAlign.LEFT,
      size: '200px',
      cellClassName: 'location-history-cell',
    });
    return columns;
  }

  function renderLocationColumn(row: ILocationHistory): ReactNode {
    const distance = memberLocationsUtils.getDistanceWhenOutsideProject(row.location, row.project?.projectInfo);
    return (
      <div className={selectedId ? (selectedId === row.location.id ? 'highlighted' : '') : ''}>
        <div className="location-header-container">
          <Label className="location-header">{memberLocationsUtils.getTypeTitle(row.type)}</Label>
          {distance ? (
            <Label className="location-distance">
              {distance} {t('mi')}
            </Label>
          ) : (
            <></>
          )}
        </div>
        <div className="pt-4 location-details">
          <div>
            <TimeAndSuperscript dateTime={dateTimeFromISOKeepZone(row.location.timeLocal)} dst={row.location.timeDst} />
          </div>
          <div className="pt-4">
            {!isSameDay(dateTimeFromISOKeepZone(row.location.timeLocal), today) &&
              getDateString(dateTimeFromISOKeepZone(row.location.timeLocal), 'LLL d', true)}
          </div>
          {row.project ? <p>{row.project.title}</p> : <></>}
        </div>
      </div>
    );
  }

  function renderEmptyState() {
    return (
      <Row justify={Justify.CENTER} align={Align.CENTER} className="empty-entry-state">
        {t('No locations.')}
      </Row>
    );
  }

  function handleRowClick(row: ILocationHistory) {
    if (onRowClick) {
      if (selectedId && selectedId === row.location.id) {
        onRowClick(undefined);
      } else {
        onRowClick(row.location.id);
      }
    }
  }

  function handlePickerSelect(value: string | null) {
    if (value) {
      setLocationFilter(value);
      if (onFilterChange) {
        onFilterChange(value);
        if (onRowClick) {
          if (selectedId) {
            onRowClick(undefined);
          }
        }
      }
    }
  }

  function getFilterTypes() {
    const types = [
      { id: MemberLocationType.ALL, name: t('All Events') },
      { id: MemberLocationType.START, name: t('Start') },
      { id: MemberLocationType.END, name: t('End') },
      { id: MemberLocationType.BREAK_START, name: t('Break Start') },
      { id: MemberLocationType.BREAK_END, name: t('Break End') },
    ];
    if (isPro) {
      types.push({ id: MemberLocationType.LOCATION_UPDATE, name: t('Location Update') });
    }
    return types;
  }

  return (
    <div className={classes}>
      <div className="location-picker pt-7 pl-4 pr-4">
        <BasicPicker
          placeholder={t('All Events')}
          onSelect={handlePickerSelect}
          value={locationFilter}
          clearable={false}
          data={getFilterTypes()}
        />
      </div>
      <div>
        <Table
          className={classes}
          cols={renderColumns()}
          data={locationFilter !== MemberLocationType.ALL ? data.filter((item) => item.type === locationFilter) : data}
          strokeCols={true}
          lazyLoad={false}
          onRowClick={handleRowClick}
          emptyTemplate={renderEmptyState()}
        />
      </div>
    </div>
  );
};

export default LocationHistoryTable;
